<template>
    <div class="tk-documents-panel">
        <div v-if="showUpload" class="tk-document-upload">
            <q-file
                v-model="selectedFile"
                outlined
                dense
                clearable
                label="Choose document"
                max-file-size="26214400"
                @rejected="onRejected"
            >
                <template v-slot:prepend>
                    <q-icon name="o_attach_file" />
                </template>
            </q-file>
            <q-input
                v-model="description"
                outlined
                dense
                autogrow
                maxlength="1000"
                label="Description"
            />
            <q-btn
                class="tk-document-upload__action"
                unelevated
                no-caps
                color="primary"
                label="Attach Document"
                :disable="!selectedFile"
                :loading="uploading"
                @click="uploadSelectedDocument"
            />
        </div>

        <template v-if="showList">
            <div class="app-data-table-toolbar">
                <q-input
                    v-model="nameSearch"
                    outlined
                    dense
                    clearable
                    debounce="200"
                    placeholder="Search attachment names..."
                    aria-label="Search attachment names"
                >
                    <template #prepend>
                        <q-icon name="search" size="20px" />
                    </template>
                </q-input>
                <q-select
                    v-model="uploadedByFilter"
                    :options="uploadedByOptions"
                    outlined
                    dense
                    clearable
                    emit-value
                    map-options
                    label="Uploaded by — any"
                />
                <q-input
                    v-model="uploadedDateFilter"
                    outlined
                    dense
                    clearable
                    stack-label
                    type="date"
                    label="Uploaded date"
                />
            </div>

            <q-table
                class="app-data-table tk-document-table"
                flat
                row-key="id"
                :rows="filteredDocuments"
                :columns="columns"
                :loading="loading"
                :pagination="{ rowsPerPage: 10 }"
                :rows-per-page-options="[10, 20, 50, 0]"
            >
                <template #body-cell-original_filename="props">
                    <q-td :props="props">
                        <button
                            class="tk-document-name"
                            type="button"
                            :title="props.row.original_filename"
                            @click="viewExistingDocument(props.row)"
                        >
                            <q-icon name="o_description" size="20px" />
                            <span>{{ props.row.original_filename }}</span>
                        </button>
                    </q-td>
                </template>
                <template #body-cell-description="props">
                    <q-td :props="props">
                        <div class="tk-document-description" :title="props.row.description || '-'">
                            {{ props.row.description || "-" }}
                        </div>
                    </q-td>
                </template>
                <template #body-cell-uploaded_by="props">
                    <q-td :props="props">{{ props.row.uploaded_by || "-" }}</q-td>
                </template>
                <template #body-cell-created_at="props">
                    <q-td :props="props">{{ formatDate(props.row.created_at) }}</q-td>
                </template>
                <template #body-cell-size_bytes="props">
                    <q-td :props="props">{{ formatSize(props.row.size_bytes) }}</q-td>
                </template>
                <template #body-cell-actions="props">
                    <q-td :props="props">
                        <div class="tk-document-actions">
                            <q-btn
                                flat
                                round
                                dense
                                color="primary"
                                icon="o_open_in_new"
                                :loading="viewingId === props.row.id"
                                @click="viewExistingDocument(props.row)"
                            >
                                <q-tooltip class="text-body2">Open</q-tooltip>
                            </q-btn>
                            <q-btn
                                flat
                                round
                                dense
                                color="primary"
                                icon="o_download"
                                :loading="downloadingId === props.row.id"
                                @click="downloadExistingDocument(props.row)"
                            >
                                <q-tooltip class="text-body2">Download</q-tooltip>
                            </q-btn>
                            <q-btn
                                v-if="showDelete"
                                flat
                                round
                                dense
                                color="negative"
                                icon="o_delete"
                                :loading="deletingId === props.row.id"
                                @click="deleteExistingDocument(props.row)"
                            >
                                <q-tooltip class="text-body2">Delete</q-tooltip>
                            </q-btn>
                        </div>
                    </q-td>
                </template>
                <template #no-data>
                    <div class="tk-documents-empty full-width">
                        <q-icon name="o_folder" size="24px" />
                        <span>{{ documents.length ? "No attachments match these filters." : "No documents attached." }}</span>
                    </div>
                </template>
            </q-table>
        </template>
    </div>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";

const formatSize = (value) => {
    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes <= 0) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });
};

const documentDateValue = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = String(date.getFullYear());
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const documentTimestamp = (value) => {
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
};

const uniqueUploadedByOptions = (documents) => [...new Set(
    documents.map(document => document.uploaded_by).filter(Boolean)
)]
    .sort((first, second) => first.localeCompare(second))
    .map(value => ({ label: value, value }));

const blobUrlFromResponse = (response) => {
    const blob = new Blob([response.data], {
        type: response.headers?.["content-type"] || "application/octet-stream",
    });
    return window.URL.createObjectURL(blob);
};

const saveBlob = (response, filename) => {
    const url = blobUrlFromResponse(response);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "document";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export default {
    name: "TkDocumentsPanel",
    emits: ["uploaded", "deleted", "count-changed"],
    props: {
        ownerType: {
            type: String,
            required: true,
            validator: value => ["person", "engagement"].includes(value),
        },
        ownerId: {
            type: [Number, String],
            required: true,
        },
        caseNoteId: {
            type: [Number, String],
            default: null,
        },
        showUpload: {
            type: Boolean,
            default: true,
        },
        showList: {
            type: Boolean,
            default: true,
        },
        showDelete: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            loading: false,
            uploading: false,
            downloadingId: null,
            deletingId: null,
            viewingId: null,
            documents: [],
            selectedFile: null,
            description: "",
            nameSearch: "",
            uploadedByFilter: null,
            uploadedDateFilter: "",
            columns: [
                { name: "original_filename", label: "File name", field: "original_filename", align: "left", sortable: true },
                { name: "description", label: "Description", field: "description", align: "left", sortable: true },
                { name: "uploaded_by", label: "Uploaded by", field: "uploaded_by", align: "left", sortable: true },
                {
                    name: "created_at",
                    label: "Uploaded date",
                    field: "created_at",
                    align: "left",
                    sortable: true,
                    sort: (first, second) => documentTimestamp(first) - documentTimestamp(second),
                },
                { name: "size_bytes", label: "Size", field: "size_bytes", align: "right", sortable: true },
                { name: "actions", label: "Actions", field: "actions", align: "right" },
            ],
        };
    },
    computed: {
        uploadedByOptions() {
            return uniqueUploadedByOptions(this.documents);
        },
        filteredDocuments() {
            const nameSearch = (this.nameSearch || "").trim().toLocaleLowerCase();
            return this.documents
                .filter(document => !nameSearch || (
                    String(document.original_filename || "").toLocaleLowerCase().includes(nameSearch)
                ))
                .filter(document => !this.uploadedByFilter || document.uploaded_by === this.uploadedByFilter)
                .filter(document => !this.uploadedDateFilter || (
                    documentDateValue(document.created_at) === this.uploadedDateFilter
                ));
        },
    },
    watch: {
        ownerId() {
            if (this.showList) this.loadDocuments();
        },
        ownerType() {
            if (this.showList) this.loadDocuments();
        },
    },
    async mounted() {
        if (this.showList) await this.loadDocuments();
    },
    methods: {
        formatDate,
        formatSize,
        async loadDocuments() {
            this.loading = true;
            try {
                const res = await api.documents.listDocuments(this.ownerType, this.ownerId, this.caseNoteId);
                this.documents = res.data || [];
                this.$emit("count-changed", this.documents.length);
            } catch (error) {
                notify.error("Failed to load documents", "top");
            } finally {
                this.loading = false;
            }
        },
        async uploadSelectedDocument() {
            if (!this.selectedFile) return;

            this.uploading = true;
            try {
                const res = await api.documents.uploadDocument(
                    this.ownerType,
                    this.ownerId,
                    this.selectedFile,
                    this.description.trim(),
                    this.caseNoteId
                );
                if (res.data) {
                    this.documents = [res.data, ...this.documents.filter(document => document.id !== res.data.id)];
                }
                this.selectedFile = null;
                this.description = "";
                this.$emit("uploaded", res.data);
                this.$emit("count-changed", this.documents.length);
                notify.primary("Document uploaded", "top");
            } catch (error) {
                const message = error?.response?.statusText;
                notify.error(message && message !== "Internal Server Error" ? message : "Failed to upload document", "top");
            } finally {
                this.uploading = false;
            }
        },
        async downloadExistingDocument(document) {
            this.downloadingId = document.id;
            try {
                const res = await api.documents.downloadDocument(this.ownerType, this.ownerId, document.id);
                saveBlob(res, document.original_filename);
            } catch (error) {
                notify.error("Failed to download document", "top");
            } finally {
                this.downloadingId = null;
            }
        },
        async viewExistingDocument(document) {
            // open synchronously so the browser doesn't treat the later navigation as a popup
            const viewerWindow = window.open("", "_blank");
            this.viewingId = document.id;
            try {
                const res = await api.documents.downloadDocument(this.ownerType, this.ownerId, document.id);
                const url = blobUrlFromResponse(res);
                if (viewerWindow) {
                    viewerWindow.location.href = url;
                } else {
                    window.location.href = url;
                }
            } catch (error) {
                if (viewerWindow) viewerWindow.close();
                notify.error("Failed to open document", "top");
            } finally {
                this.viewingId = null;
            }
        },
        async deleteExistingDocument(document) {
            this.deletingId = document.id;
            try {
                await api.documents.deleteDocument(this.ownerType, this.ownerId, document.id);
                this.documents = this.documents.filter(existingDocument => existingDocument.id !== document.id);
                this.$emit("deleted", document);
                this.$emit("count-changed", this.documents.length);
                notify.primary("Document deleted", "top");
            } catch (error) {
                notify.error("Failed to delete document", "top");
            } finally {
                this.deletingId = null;
            }
        },
        onRejected() {
            notify.error("Document must be 25 MB or smaller", "top");
        },
    },
};
</script>

<style scoped>
.tk-documents-panel {
    position: relative;
    max-width: 100%;
    min-height: 70px;
}

.tk-document-upload {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    align-items: start;
    margin-bottom: 14px;
    max-width: 100%;
}

.tk-document-upload > * {
    min-width: 0;
}

.tk-document-upload :deep(.q-field) {
    min-width: 0;
}

.tk-document-upload__action {
    grid-column: 1 / -1;
    justify-self: end;
}

.tk-documents-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 140px;
    padding: 20px;
    color: #748078;
    line-height: 1.4;
}

.tk-document-name {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    max-width: 300px;
    padding: 0;
    overflow: hidden;
    border: 0;
    background: transparent;
    cursor: pointer;
    font: inherit;
    color: var(--q-primary);
    font-weight: 550;
    text-align: left;
}

.tk-document-name span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tk-document-name:hover span {
    text-decoration: underline;
}

.tk-document-description {
    max-width: 340px;
    overflow: hidden;
    color: #5f6d66;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tk-document-actions {
    display: flex;
    flex-direction: row;
    gap: 2px;
    justify-content: flex-end;
    white-space: nowrap;
}

@media (max-width: 700px) {
    .tk-document-upload {
        grid-template-columns: 1fr;
    }

    .tk-document-upload__action {
        width: 100%;
    }
}
</style>
